/**
 * Rótulos e helpers de apresentação do Playbook (Brief 4 · Tela 1).
 *
 * Mapeia enums do serializer real (vertical/status) para rótulos PT-BR e
 * deriva a contagem de etapas defensivamente (wire-shape `{ list: [...] }`
 * vs array — ver types.ts).
 */

import type {
  NativeSchedulingMode,
  PlaybookAutonomyPhase,
  PlaybookOperationMode,
  PlaybookProactiveAppetite,
  PlaybookStatus,
  PlaybookValidationMode,
  PlaybookVertical,
  StepConfirmation,
  StepEnforcement,
  StepObligation,
  WaitlistPromotionMode,
} from './types';

export const VERTICAL_LABELS: Record<PlaybookVertical, string> = {
  support: 'Suporte',
  commercial: 'Comercial',
  retention: 'Retenção',
  collections: 'Cobrança',
  triage: 'Triagem',
  onboarding: 'Onboarding',
  satisfaction_survey: 'Pesquisa de satisfação',
  legal: 'Jurídico',
  healthcare: 'Saúde',
  other: 'Outro',
};

export function verticalLabel(vertical: string): string {
  return VERTICAL_LABELS[vertical as PlaybookVertical] ?? vertical;
}

export const STATUS_LABELS: Record<PlaybookStatus, string> = {
  active: 'Ativo',
  draft: 'Rascunho',
  deprecated: 'Descontinuado',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as PlaybookStatus] ?? status;
}

/**
 * Modificador visual do pill (compounds-4: --active / --draft / --inactive).
 * `deprecated` reaproveita o visual neutro de `inactive` do gabarito.
 */
export function statusPillModifier(status: string): 'active' | 'draft' | 'inactive' {
  if (status === 'active') return 'active';
  if (status === 'draft') return 'draft';
  return 'inactive';
}

/**
 * Normaliza o wire-shape duplo (`{ list: [...] }` vs array puro — R15 ao vivo)
 * para um array. Base de `countSteps` e da leitura de etapas/quick replies.
 */
export function readList<T = unknown>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value !== null && typeof value === 'object' && 'list' in value) {
    const { list } = value;
    if (Array.isArray(list)) return list as T[];
  }
  return [];
}

/** Conta etapas tolerando ambos os shapes: `unknown[]` (DTO) e `{ list: [...] }` (wire). */
export function countSteps(steps: unknown): number {
  return readList(steps).length;
}

/**
 * Item 368 F2 — Fases de autonomia da IA no playbook.
 * Verticals 'retention' | 'collections' | 'legal' bloqueiam proposal/autonomous
 * no backend; a UI reflete isso via `RISK_VERTICALS_FE`.
 */
export const AUTONOMY_PHASE_LABELS: Record<PlaybookAutonomyPhase, { label: string; desc: string }> =
  {
    manual: {
      label: 'Manual — a IA não conduz',
      desc: 'O atendente conduz; a IA apenas sugere.',
    },
    proposal: {
      label: 'Proposta — a IA pede para assumir',
      desc: 'A conversa nasce com o atendente; a IA propõe assumir as de baixo risco e você decide. Cada decisão ajuda o playbook a se formar.',
    },
    autonomous: {
      label: 'Autônomo — a IA conduz',
      desc: 'O playbook se formou: a IA conduz desde o início (Professional+); em planos sem H_c, nasce com o atendente.',
    },
  };

/**
 * Verticals de risco: backend rejeita proposal/autonomous nesses verticals.
 * Espelha `RISK_VERTICALS` de apps/engine/src/modules/playbooks/playbooks.service.ts.
 */
export const RISK_VERTICALS_FE = new Set<string>(['retention', 'collections', 'legal']);

export function autonomyPhaseLabel(phase: string): { label: string; desc: string } {
  return AUTONOMY_PHASE_LABELS[phase as PlaybookAutonomyPhase] ?? { label: phase, desc: '' };
}

/**
 * Modos de operação (ADR-030). O backend tem 4 modos (H_a..H_d); o mockup só
 * mostrava 3 — aqui mapeamos os 4 reais com rótulo + descrição honestos.
 */
export const OPERATION_MODE_LABELS: Record<PlaybookOperationMode, { label: string; desc: string }> =
  {
    H_a: {
      label: 'H_a — Atendente conduz',
      desc: 'O humano assume desde o início; a IA apenas sugere.',
    },
    H_b: {
      label: 'H_b — IA inicia, humano assume',
      desc: 'A IA inicia o atendimento; o humano assume quando o cliente responde.',
    },
    H_c: {
      label: 'H_c — IA conduz',
      desc: 'A IA conduz de forma autônoma; o humano entra apenas se necessário.',
    },
    H_d: {
      label: 'H_d — Meta-frame',
      desc: 'Quadro configurável: o tenant define como os modos se combinam.',
    },
  };

export function operationModeLabel(mode: string): { label: string; desc: string } {
  return OPERATION_MODE_LABELS[mode as PlaybookOperationMode] ?? { label: mode, desc: '' };
}

/** Modos de validação (none | ai_optional | ai_required — únicos do backend). */
export const VALIDATION_MODE_LABELS: Record<
  PlaybookValidationMode,
  { label: string; desc: string }
> = {
  none: {
    label: 'Sem validação',
    desc: 'As respostas da IA seguem sem checagem adicional.',
  },
  ai_optional: {
    label: 'Validação por IA (opcional)',
    desc: 'A IA pode validar antes do envio, sem bloquear.',
  },
  ai_required: {
    label: 'Validação por IA (obrigatória)',
    desc: 'Toda resposta passa por validação da IA antes do envio.',
  },
};

export function validationModeLabel(mode: string): { label: string; desc: string } {
  return VALIDATION_MODE_LABELS[mode as PlaybookValidationMode] ?? { label: mode, desc: '' };
}

/** Apetite proativo (none | conservative | balanced | active — únicos do backend). */
export const APPETITE_LABELS: Record<PlaybookProactiveAppetite, { label: string; desc: string }> = {
  none: {
    label: 'Nenhum',
    desc: 'O playbook não dispara abordagens proativas.',
  },
  conservative: {
    label: 'Conservador',
    desc: 'Aborda proativamente apenas em oportunidades de alta confiança.',
  },
  balanced: {
    label: 'Balanceado',
    desc: 'Equilíbrio entre proatividade e contenção.',
  },
  active: {
    label: 'Ativo',
    desc: 'Aborda proativamente sempre que houver oportunidade pertinente.',
  },
};

export function appetiteLabel(appetite: string): { label: string; desc: string } {
  return APPETITE_LABELS[appetite as PlaybookProactiveAppetite] ?? { label: appetite, desc: '' };
}

/**
 * Renderiza uma ação de etapa como texto. Tolera string (NetComet) e objeto
 * `{ type, template|target|keywords }` (triagem) — R15 ao vivo. Sem fabricar:
 * apenas reapresenta o que o backend entrega.
 */
export function formatStepAction(action: unknown): string {
  if (typeof action === 'string') return action;
  if (action !== null && typeof action === 'object') {
    const a = action as Record<string, unknown>;
    const type = typeof a.type === 'string' ? a.type : 'ação';
    const detail = a.template ?? a.target ?? a.keywords;
    if (Array.isArray(detail)) return `${type}: ${detail.join(', ')}`;
    if (typeof detail === 'string') return `${type}: ${detail}`;
    return type;
  }
  return String(action);
}

/**
 * Rótulos PT-BR dos outcomes de auto-close (catálogo ADR-054 — formas kebab do
 * conductor e snake_case dos seeds/config de tenant). Outcome desconhecido
 * degrada para o id cru — sem inventar rótulo.
 */
const AUTO_CLOSE_OUTCOME_LABELS: Record<string, string> = {
  'information-provided': 'Informação fornecida',
  'objective-fulfilled': 'Objetivo cumprido',
  'customer-served': 'Cliente atendido',
  'service-completed': 'Serviço concluído',
  'transaction-completed': 'Transação concluída',
  // 07/Ago — o único desfecho NÃO-resolvido que a IA pode emitir. O rótulo diz
  // quem encerrou de propósito: é o discriminador que faltava (se foi a casa que
  // travou, a conversa tem de continuar aberta; se foi o cliente que se despediu,
  // manter aberto só convoca lembrete automático horas depois).
  'customer-ended': 'Cliente encerrou',
  objective_fulfilled: 'Objetivo cumprido',
  out_of_scope: 'Fora de escopo',
  abandoned_by_customer: 'Cliente abandonou',
  resolved: 'Resolvido',
};

export function autoCloseOutcomeLabel(outcome: string): string {
  return AUTO_CLOSE_OUTCOME_LABELS[outcome] ?? outcome;
}

/**
 * Rótulos PT-BR da banda qualitativa de aceitação (Item 368 F2 Fatia C).
 * Banda NEUTRA e descritiva — não decide graduação. `none`/`insufficient`
 * têm tratamento próprio na UI (estado vazio / "amostra pequena").
 */
export const BAND_LABELS: Record<'high' | 'medium' | 'low', string> = {
  high: 'aceitação alta',
  medium: 'aceitação média',
  low: 'aceitação baixa',
};

/**
 * Frente B Plano 3 (B2) — opções do override de agenda nativa por-playbook.
 * `null` = herda o `Tenant.nativeSchedulingModeDefault` (cascade Cliente▸Playbook▸Tenant).
 */
export const NATIVE_SCHEDULING_MODE_OPTIONS: readonly {
  value: NativeSchedulingMode | null;
  label: string;
  desc: string;
}[] = [
  { value: null, label: 'Herdar do tenant', desc: 'Usa o modo default configurado no tenant.' },
  {
    value: 'propose_and_book',
    label: 'A IA agenda sozinha',
    desc: 'Fecha o horário direto na conversa com o cliente verificado.',
  },
  {
    value: 'propose_only',
    label: 'A IA propõe; humano confirma',
    desc: 'A IA sugere horários; o agendamento final passa por um atendente.',
  },
  {
    value: 'qualify_only',
    label: 'A IA só qualifica',
    desc: 'A IA levanta o interesse e encaminha; não sugere nem agenda.',
  },
];

/**
 * Frente B Plano 3 (B2) — opções do override do modo de promoção da fila por-playbook.
 * `null` = herda o `Tenant.waitlistPolicy.promotionMode`.
 */
export const WAITLIST_PROMOTION_MODE_OPTIONS: readonly {
  value: WaitlistPromotionMode | null;
  label: string;
  desc: string;
}[] = [
  { value: null, label: 'Herdar do tenant', desc: 'Usa a política de fila do tenant.' },
  { value: 'sequential', label: 'Um a um', desc: 'Oferta por ordem da fila, um de cada vez.' },
  { value: 'broadcast', label: 'A todos', desc: 'Oferta a todos os elegíveis ao mesmo tempo.' },
  { value: 'manual', label: 'Manual', desc: 'O operador libera quem entra da fila.' },
];

/**
 * Frente C — eixos de governança por-etapa (editor de steps). Rótulos legíveis
 * ao operador; os `value` batem com os literais canônicos do parser do motor
 * (`ai-conductor/step-governance.ts`). "Sugestão" = `flexivel` (SEM acento).
 */
export const ENFORCEMENT_OPTIONS: readonly {
  value: StepEnforcement;
  label: string;
  desc: string;
}[] = [
  {
    value: 'flexivel',
    label: 'Sugestão',
    desc: 'A IA considera a etapa, mas decide sozinha. Sem imposição.',
  },
  {
    value: 'guiado',
    label: 'Guiado',
    desc: 'A IA segue o protocolo no raciocínio (via prompt). Sem bloqueio de código.',
  },
  {
    value: 'duro',
    label: 'Duro',
    desc: 'O sistema pode BLOQUEAR a ação travada até a etapa ser cumprida (gate determinístico — arma com obrigatoriedade + sinal de sucesso).',
  },
];

/**
 * Rótulos PT-BR das ações do conductor que uma etapa dura pode bloquear
 * (vocabulário genérico do motor). Desconhecida (ex.: nome de ferramenta) volta
 * crua — sem inventar rótulo.
 */
const CONDUCTOR_ACTION_LABELS: Record<string, string> = {
  escalate: 'escalar',
  close_conversation: 'encerrar',
};

export function conductorActionLabel(action: string): string {
  return CONDUCTOR_ACTION_LABELS[action] ?? action;
}

export const OBLIGATION_OPTIONS: readonly {
  value: StepObligation;
  label: string;
  desc: string;
}[] = [
  { value: 'optional', label: 'Opcional', desc: 'A etapa pode ser pulada.' },
  {
    value: 'conditional',
    label: 'Condicional',
    desc: 'Obrigatória conforme o contexto da conversa.',
  },
  { value: 'required', label: 'Obrigatória', desc: 'A etapa precisa ser cumprida.' },
];

export const CONFIRMATION_OPTIONS: readonly {
  value: StepConfirmation;
  label: string;
  desc: string;
}[] = [
  { value: 'none', label: 'Nenhuma', desc: 'Não exige confirmação de que a etapa foi cumprida.' },
  {
    value: 'implicit',
    label: 'Implícita',
    desc: 'A própria execução (ex.: a ferramenta rodar) já confirma.',
  },
  {
    value: 'double-check',
    label: 'Dupla checagem',
    desc: 'Pede corroboração por um sinal independente (ex.: a ferramenta retornar resultado), não só a tentativa.',
  },
];
